import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const PayrollCalculator = () => {
  // State variables
  const [grossPay, setGrossPay] = useState("");
  const [taxes, setTaxes] = useState("");
  const [deductions, setDeductions] = useState("");
  const [netPay, setNetPay] = useState(0);

  // Recalculate net pay whenever any of the inputs change
  useEffect(() => {
    // Calculate net pay as Gross Pay - (Taxes + Deductions)
    setNetPay(grossPay - (taxes + deductions));

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [grossPay, taxes, deductions]); // Dependencies: will run when any of these values change

  // Event handlers
  const handleGrossPayChange = (e) =>
    setGrossPay(parseFloat(e.target.value) || 0);
  const handleTaxesChange = (e) => setTaxes(parseFloat(e.target.value) || 0);
  const handleDeductionsChange = (e) =>
    setDeductions(parseFloat(e.target.value) || 0);

  return (
    <Layout title="Payroll Calculator" path="payroll">
      <section className="section-padding" id="payroll-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Payroll Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Payroll Calculator</h3>
                <div className="container">
                  {/* Input Fields for Payroll Calculation */}
                  <div className="row gy-3">
                    {/* Gross Pay */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="grossPay" className="form-label">
                        Gross Pay (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Gross Pay:</strong> The total earnings before any deductions, including taxes and benefits."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="grossPay"
                        placeholder="Enter gross pay"
                        value={grossPay}
                        onChange={handleGrossPayChange}
                      />
                      <small className="form-text text-muted">
                        Enter the total earnings before deductions
                      </small>
                    </div>

                    {/* Taxes */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="taxes" className="form-label">
                        Taxes (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Taxes:</strong> The total amount withheld for income tax, social security, and other mandatory contributions."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="taxes"
                        placeholder="Enter taxes"
                        value={taxes}
                        onChange={handleTaxesChange}
                      />
                      <small className="form-text text-muted">
                        Enter the total tax deductions
                      </small>
                    </div>

                    {/* Deductions */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="deductions" className="form-label">
                        Deductions (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Deductions:</strong> Other non-tax deductions such as retirement contributions, insurance, or garnishments."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="deductions"
                        placeholder="Enter deductions"
                        value={deductions}
                        onChange={handleDeductionsChange}
                      />
                      <small className="form-text text-muted">
                        Enter any additional deductions
                      </small>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Net Pay:{" "}
                        <span className="fw-bold ms-3">
                          {netPay.toFixed(2)}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Payroll Calculator
                </h3>

                {/* What is a Payroll Calculator? */}
                <h4 className="mt-3 mb-3">What is a Payroll Calculator?</h4>
                <p>
                  A **Payroll Calculator** helps businesses determine an
                  employee's net pay by accounting for deductions, taxes, and
                  other factors. It ensures accuracy in payroll processing,
                  making the task of calculating salaries more efficient.
                </p>

                {/* Why Use a Payroll Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Payroll Calculator?</h4>
                <p>There are several benefits to using a payroll calculator:</p>
                <ul>
                  <li>
                    <strong>Ensures Accuracy in Payroll Processing:</strong>{" "}
                    Helps calculate the exact net pay by factoring in
                    deductions, taxes, and benefits.
                  </li>
                  <li>
                    <strong>
                      Saves Time and Reduces Administrative Errors:
                    </strong>
                    Automates payroll calculation, reducing the time spent on
                    manual calculations and minimizing errors.
                  </li>
                  <li>
                    <strong>Supports Compliance with Tax Regulations:</strong>
                    Ensures that tax deductions and other compliance
                    requirements are properly applied.
                  </li>
                </ul>

                {/* Suitable for */}
                <h4 className="mt-4 mb-3">
                  Who is this Calculator Suitable For?
                </h4>
                <p>This calculator is ideal for:</p>
                <ul>
                  <li>
                    <strong>HR Departments</strong>
                  </li>
                  <li>
                    <strong>Payroll Services</strong>
                  </li>
                  <li>
                    <strong>Businesses of All Sizes</strong>
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is the purpose of calculating net pay?
                </h4>
                <p>
                  The purpose of calculating net pay is to determine the actual
                  amount an employee receives after all deductions, taxes, and
                  other adjustments are made from their gross salary.
                </p>

                <h4 className="mt-4 mb-3">
                  What are common deductions from gross pay?
                </h4>
                <p>
                  Common deductions include taxes, retirement contributions,
                  insurance premiums, and other employee benefits.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PayrollCalculator;
